<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import BarChart from './BarChart.vue';
import SpeechBubbleTailDown from '@/components/svg/SpeechBubbleTailDown.vue';
import ContentsTitle from './ContentsTitle.vue';

const props = defineProps({
  userName: {
    type: String,
    default: '',
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object,
    default: () => ({}),
  },
});

// 데이터 접근
const 에너지추천 = computed(() => props.data?.에너지추천 || {});
const 안정에너지 = computed(() => props.data?.안정에너지 || {});

// 왼쪽 영역 데이터
const 추천운세 = computed(() => 에너지추천.value?.추천운세 || []);
const 권장요약 = computed(() => 에너지추천.value?.권장요약 || '');
const 권장생활 = computed(() => 에너지추천.value?.권장생활 || []);
const 권장조언 = computed(() => 에너지추천.value?.권장조언 || '');

// 모바일용 권장조언 (', ' 기준 줄바꿈)
const 권장조언Display = computed(() => {
  if (!권장조언.value) return '';
  if (props.isMobile) {
    return 권장조언.value.replace(/, /g, ',<br>');
  }
  return 권장조언.value;
});

// 오른쪽 영역 데이터 (안정에너지)
const 주의운세 = computed(() => 안정에너지.value?.주의운세 || []);
const 안정요약 = computed(() => 안정에너지.value?.안정요약 || '');
const 안정생활 = computed(() => 안정에너지.value?.안정생활 || []);
const 안정조언 = computed(() => 안정에너지.value?.안정조언 || '');

// 모바일용 안정조언 (', ' 기준 줄바꿈)
const 안정조언Display = computed(() => {
  if (!안정조언.value) return '';
  if (props.isMobile) {
    return 안정조언.value.replace(/, /g, ',<br>');
  }
  return 안정조언.value;
});

// 디버깅용 로그
watch(
  () => props.data,
  (newData) => {
    console.log('Contents07 props.data:', newData);
  },
  { immediate: true, deep: true },
);
</script>

<template>
  <VRow class="full-height over-hidden">
    <!-- 왼쪽 컬럼 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <!-- 타이틀 -->
        <ContentsTitle
          :number="5"
          text="올해, 나에게 유리한 방향"
          :isMobile="props.isMobile"
        />

        <div class="recommend-wrapper" :class="{ mobile: props.isMobile }">
          <!-- 권장조언 말풍선 -->
          <div class="advice-bubble" v-if="권장조언">
            <div class="bubble-content" v-html="권장조언Display"></div>
            <SpeechBubbleTailDown class="bubble-tail" />
          </div>

          <!-- 에너지 활용이 수월한 영역 - 바 차트 -->
          <div class="recommend-card" v-if="추천운세.length">
            <div class="card-title">🧘🏻‍♀ 에너지 활용이 수월한 영역</div>
            <BarChart :data="추천운세" />
          </div>

          <!-- 올해 흐름에 맞는 활용 방향 -->
          <div class="recommend-card" v-if="권장요약">
            <div class="card-title">🌊 올해 흐름에 맞는 활용 방향</div>
            <div class="card-content" v-html="transformYearlyFortuneText(권장요약)"></div>
          </div>

          <!-- 올해 활용하면 좋은 권장 에너지 -->
          <div class="recommend-card" v-if="권장생활.length">
            <div class="card-title">💐 올해 활용하면 좋은 권장 에너지</div>
            <div class="life-list">
              <div
                v-for="(item, idx) in 권장생활"
                :key="idx"
                class="life-item"
                v-html="transformYearlyFortuneText(item)"
              ></div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>

    <!-- 오른쪽 컬럼 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <!-- 타이틀 -->
        <ContentsTitle
          :number="6"
          text="올해, 에너지를 아껴도 괜찮은 방향"
          :isMobile="props.isMobile"
        />

        <div class="stable-wrapper" :class="{ mobile: props.isMobile }">
          <!-- 안정조언 말풍선 -->
          <div class="advice-bubble red" v-if="안정조언">
            <div class="bubble-content" v-html="안정조언Display"></div>
            <SpeechBubbleTailDown class="bubble-tail" fillColor="#fff5f5" strokeColor="#be3331" />
          </div>

          <!-- 속도 조절이 필요한 영역 - 바 차트 -->
          <div class="stable-card" v-if="주의운세.length">
            <div class="card-title">🏃🏻‍♀️ 속도 조절이 필요한 영역</div>
            <BarChart :data="주의운세" barColor="#be3331" />
          </div>

          <!-- 안정적으로 이어가기 위한 방향 -->
          <div class="stable-card" v-if="안정요약">
            <div class="card-title">🍵 안정적으로 이어가기 위한 방향</div>
            <div class="card-content" v-html="transformYearlyFortuneText(안정요약)"></div>
          </div>

          <!-- 에너지를 아끼는 선택 -->
          <div class="stable-card" v-if="안정생활.length">
            <div class="card-title">🔋 에너지를 아끼는 선택</div>
            <div class="life-list">
              <div
                v-for="(item, idx) in 안정생활"
                :key="idx"
                class="life-item"
                v-html="transformYearlyFortuneText(item)"
              ></div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
.recommend-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  padding-bottom: 130px;
  overflow-y: auto;
  background: #fff;

  &.mobile {
    padding: 20px 0;
    padding-bottom: 24px;
    gap: 24px;

    .advice-bubble {
      padding: 0 16px;
    }

    .recommend-card {
      border-radius: 0;
    }
  }
}

// 말풍선 스타일
.advice-bubble {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 8px;

  .bubble-content {
    padding: 16px 24px;
    border-radius: 12px;
    border: 1px solid #88c34a;
    background: #f9fcf6;
    color: #88c34a;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    text-align: center;
  }

  .bubble-tail {
    margin-top: -1px;
  }
}

// 카드 스타일
.recommend-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  background: #f9fcf6;
}

.card-title {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}

.card-content {
  padding: 12px 16px;
  background: #fff;
  border-radius: 8px;
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
}

// 권장생활 리스트
.life-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.life-item {
  padding: 12px 16px;
  background: #fff;
  border-radius: 8px;
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
}

// ========== 오른쪽 영역 (안정에너지) ==========
.stable-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  padding-bottom: 130px;
  overflow-y: auto;
  background: #fff;

  &.mobile {
    padding: 20px 0;
    padding-bottom: 24px;
    gap: 24px;

    .advice-bubble {
      padding: 0 16px;
    }

    .stable-card {
      border-radius: 0;
    }
  }
}

// 빨간색 말풍선
.advice-bubble.red {
  .bubble-content {
    border-color: #be3331;
    background: #fff5f5;
    color: #be3331;
  }
}

// 안정 카드 스타일
.stable-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  background: #fff5f5;
}
</style>
